import EquationInput from "../../../../components/inputs/EquationInput";
import { BlockMath, InlineMath } from "react-katex";
import { checkCircleEquation } from "../../../../utils/circleEquation";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";

export const id = "WriteCircleFromDiameterTask";
export const displayName = "Równanie okręgu na podstawie średnicy";

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatShifted(variable, value) {
  if (value === 0) return `${variable}^2`;
  if (value > 0) return `(${variable} - ${value})^2`;
  return `(${variable} + ${Math.abs(value)})^2`;
}

const INTEGER_RADIUS_OFFSETS = [
  [3, 4],
  [4, 3],
  [6, 8],
  [8, 6],
  [5, 12],
  [12, 5],
];

const RADICAL_RADIUS_OFFSETS = Array.from({ length: 6 }, (_, x) => x + 1)
  .flatMap((x) => Array.from({ length: 6 }, (_, y) => [x, y + 1]))
  .filter(([x, y]) => x !== y && !Number.isInteger(Math.sqrt(x ** 2 + y ** 2)));

function getRadiusOffset(usesRadicalRadius) {
  const offsets = usesRadicalRadius ? RADICAL_RADIUS_OFFSETS : INTEGER_RADIUS_OFFSETS;
  const [absoluteDx, absoluteDy] = offsets[getRandomInt(0, offsets.length - 1)];
  return {
    dx: absoluteDx * (getRandomInt(0, 1) === 0 ? -1 : 1),
    dy: absoluteDy * (getRandomInt(0, 1) === 0 ? -1 : 1),
  };
}

function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside = inside / (i * i);
      outside = outside * i;
    }
  }
  return { outside, inside };
}


export function generateTask() {
  const usesRadicalRadius = Math.random() < 0.2;
  const S = {
    x: getRandomInt(-5, 5),
    y: getRandomInt(-5, 5),
  };
  const radiusOffset = getRadiusOffset(usesRadicalRadius);
  const A = { x: S.x - radiusOffset.dx, y: S.y - radiusOffset.dy };
  const B = { x: S.x + radiusOffset.dx, y: S.y + radiusOffset.dy };

  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const diameterSquared = dx * dx + dy * dy;
  const radiusSquared = radiusOffset.dx ** 2 + radiusOffset.dy ** 2;
  const numericRadius = Math.sqrt(radiusSquared);

  const simplified = simplifySqrt(diameterSquared);
  const numerator = simplified.outside;
  const inside = simplified.inside;

  const radiusLatex =
    inside === 1
      ? `\\dfrac{${numerator}}{2}`
      : `\\dfrac{${numerator === 1 ? "" : numerator}\\sqrt{${inside}}}{2}`;

  // przygotuj answerLatex tak samo jak w renderAnswer
  const answerLatex = `${formatShifted("x", S.x)} + ${formatShifted("y", S.y)} = ${radiusSquared}`;

  return {
    type: "WriteCircleFromDiameterTask",
    data: {
      A,
      B,
      S,
      dx,
      dy,
      radiusSquared,
      numericRadius,
      radiusLatex,
      radiusKind: usesRadicalRadius ? "radical" : "integer",
    },
    answer: answerLatex,
  };
}


export function renderTask(task) {
  const { A, B } = task.data;
  return (
    <div className="space-y-4">
      <p>
        Wyznacz równanie okręgu, którego średnicą jest odcinek o końcach{" "}
        <InlineMath math={`A = (${A.x},\\ ${A.y})`} /> i{" "}
        <InlineMath math={`B = (${B.x},\\ ${B.y})`} />.
      </p>
    </div>
  );
}

export function renderAnswer(task) {
  const { S, radiusSquared } = task.data;
  const answerLatex = `${formatShifted("x", S.x)} + ${formatShifted("y", S.y)} = ${radiusSquared}`;

  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      <div style={{ display: "inline-block" }}>
        <InlineMath math={answerLatex} />
      </div>
    </div>
  );
}



export function renderInput(task, userInput, setUserInput, disabled) {
  return (
    <EquationInput
      value={userInput || ""}
      onChange={(val) => setUserInput(val)}
      disabled={disabled}
      placeholder="(x - a)^2 + (y - b)^2 = r^2"
    />
  );
}

export function renderUserAnswer(userInput) {
  if (!userInput || typeof userInput !== "string" || userInput.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const toLatexFraction = (str) =>
    str
      .replace(/(\d+)\s+(\d+)\/(\d+)/g, (_, whole, num, denom) => {
        return `${whole}\\dfrac{${num}}{${denom}}`;
      })
      .replace(/(?<!\\dfrac\{)(\d+)\/(\d+)/g, (_, num, denom) => {
        return `\\dfrac{${num}}{${denom}}`;
      });

  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ display: "inline-block" }}>
        <BlockMath math={toLatexFraction(userInput)} />
      </div>
    </div>
  );
}


export function renderExplanation({ data, answer }) {
  const { A, B, S, radiusSquared, numericRadius } = data;
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [{ center: S, radius: numericRadius }],
    points: [A, B, S],
  });


  if (!A || !B || !S || radiusSquared == null || isNaN(radiusSquared)) {
    return (
      <div style={{ color: "red" }}>
        Błąd danych – brak wymaganego elementu (A, B, S, radiusSquared)
      </div>
    );
  }

  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const dxLatex = dx < 0 ? `(${dx})` : `${dx}`;
  const dyLatex = dy < 0 ? `(${dy})` : `${dy}`;

  const radiusSqLatex = formatMixedFractionLatex(radiusSquared);
  const xPart = formatShifted("x", S.x);
  const yPart = formatShifted("y", S.y);
  const wrapIfNegative = (n) => (n < 0 ? `(${n})` : `${n}`);
  const wrapIfNegativeExplicit = (n) => (n < 0 ? `(${n})` : `${n}`);

const withParensIfNegative = (n) => (n < 0 ? `(${n})` : `${n}`);

const dx2 = dx ** 2;
const dy2 = dy ** 2;
const underRoot = dx2 + dy2;

const simplified = simplifySqrt(underRoot) || { outside: "?", inside: "?" };
const numerator = simplified.outside;
const inside = simplified.inside;

const latexSimplified =
  inside === 1
    ? `\\dfrac{${numerator}}{2}`
    : `\\dfrac{${numerator === 1 ? "" : numerator}\\sqrt{${inside}}}{2}`;


return (
  <div className="space-y-4" style={{ textAlign: "left", marginTop: "1rem" }}>
    <div>
      <p>
        <strong>Krok 1:</strong> Obliczamy środek odcinka{" "}
        <InlineMath math="\overline{AB}" /> jako punkt <InlineMath math="S" />:
      </p>
<BlockMath
  math={
    `S = \\left(\\frac{${withParensIfNegative(A.x)} + ${withParensIfNegative(B.x)}}{2},\\ \\frac{${withParensIfNegative(A.y)} + ${withParensIfNegative(B.y)}}{2}\\right)` +
    `= \\left(\\frac{${A.x + B.x}}{2},\\ \\frac{${A.y + B.y}}{2}\\right)` +
    `= (${S.x},\\ ${S.y})`
  }
/>
      </div>

<div>
  <strong>Krok 2:</strong> Liczymy promień jako połowę długości odcinka:
  <BlockMath
    math={`r = \\frac{1}{2} \\cdot \\sqrt{(${wrapIfNegative(B.x)} - ${wrapIfNegative(A.x)})^2 + (${wrapIfNegative(B.y)} - ${wrapIfNegative(A.y)})^2}`}
  />
  <BlockMath
    math={`r = \\frac{1}{2} \\cdot \\sqrt{${dxLatex}^2 + ${dyLatex}^2}`}
  />
  <BlockMath
    math={`r = \\frac{1}{2} \\cdot \\sqrt{${dx2} + ${dy2}}`}
  />

  {(() => {
    const rootExpr = `\\dfrac{\\sqrt{${underRoot}}}{2}`;
    const simplified = simplifySqrt(underRoot);
    const outside = simplified?.outside;
    const inside = simplified?.inside;

    let finalLine = `r = ${rootExpr}`;

    if (
      typeof outside === "number" &&
      typeof inside === "number" &&
      !isNaN(outside) &&
      !isNaN(inside)
    ) {
      if (inside === 1) {
        const numeric = formatMixedFractionLatex(outside / 2);
        finalLine += ` = ${numeric}`;
      } else {
        const simplifiedLatex = `\\dfrac{${outside === 1 ? "" : outside}\\sqrt{${inside}}}{2}`;
        finalLine += ` = ${simplifiedLatex}`;
        if (outside === 2) {
          finalLine += ` = \\sqrt{${inside}}`;
        }
      }
    }

    return <BlockMath math={finalLine} />;
  })()}
</div>


<div>
  <strong>Krok 3:</strong> Równanie okręgu ma postać:
  <BlockMath math="(x - a)^2 + (y - b)^2 = r^2" />
  <BlockMath math={`${xPart} + ${yPart} = ${radiusSqLatex}`} />
</div>

<div>
  <strong>Wizualizacja:</strong>
  <div style={{ display: "flex", justifyContent: "center" }}>
  <GraphViewer
    points={{
      A: { ...A, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
      B: { ...B, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
      S: { ...S, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
    }}
    segments={[
  {
    p1: "A",
    p2: "B",
    label: "|AB|",
    color: CIRCLE_GRAPH_COLORS.segment,
  },
]}
    circles={[
      {
        center: S,
        radius: numericRadius,
        color: CIRCLE_GRAPH_COLORS.circle,
        label: "okrąg",
      },
    ]}
    xRange={xRange}
    yRange={yRange}
    width={graphSize}
    height={graphSize}
    showGrid
  />
  </div>
</div>
    </div>
  );
}

export function checkAnswer(task, userInput) {
  return checkCircleEquation(userInput, task.answer);
}
